/* eslint-disable camelcase */

import { useTranslation } from 'react-i18next';

import {
  Recommendation,
  RecommendationCardEnum,
  RecommendationCardType,
  RecommendationProductCode,
} from '@/api/rest/resources/types/recommendations';
import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useRecommendations } from '@/hooks/useRecommendations';
import { isNullish } from '@/utils/booleans';

type SummaryProps = {
  productCode?: RecommendationProductCode;
};

export const useSummaryFromRecommendation = (props: SummaryProps | null) => {
  const { t } = useTranslation();
  const recommendations = useRecommendations().data?.recommendations ?? [];

  if (isNullish(props)) return null;
  if (isNullish(props.productCode)) return null;

  const productRecommendations = recommendations.filter((rec) => rec.recommendation_product_code === props.productCode);

  return {
    name: DefaultWidgetNames.default_summary,
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {},
    widgets: productRecommendations.map((recommendation) => ({
      name: DefaultWidgetNames.default_recommendation_summary,
      type: WidgetType.o_card,
      relatedData: {
        header: t('global.widget.summary.title'),
        body: getRecommendationSummaryText(recommendation),
        styles: {
          root: 'border-none w-full',
          header: 'border-none typography-h3 text-text-primary px-0',
          body: 'border border-neutral-black-12 rounded-2xl flex-col flex gap-4 whitespace-pre-wrap',
        },
      },
      visible: true,
    })),
  } as WidgetSlotConfiguration;
};

const getRecommendationSummaryText = (recommendation: Recommendation): string => {
  const { recommendation_data } = recommendation;

  const { body, planning, body_expanded, recommendation_type } = recommendation_data;

  const recommendationType = recommendation_type as RecommendationCardType;

  if (!recommendationType || !RecommendationCardEnum[`${recommendationType}`]) {
    throw new Error(`Invalid recommendation type: ${recommendation_type}`);
  }
  const bodyText = body_expanded?.body || body;
  const planningText = planning?.plan_body;
  return `${bodyText}\n\n${planningText}`;
};
